// Pointer Lock for a match: the pure half of "keep the cursor in the window" — the virtual cursor's math
// and the lock state machine, both DOM-free and driven through a small `LockApi` port so tests can fake
// the browser. virtualPointer.ts is the DOM half that uses them.
//
// Lock lifecycle (createLockMachine):
//   off ── setActive(true) ──▶ idle ── press() ──▶ pending ── change(locked) ──▶ locked
//   pending ── error / change(unlocked) ──▶ idle      locked ── change(unlocked) ──▶ idle (forced: Esc, alt-tab)
//   any ── setActive(false) ──▶ off (exits the lock itself; a grant that lands later is exited at once)
// requestPointerLock needs a user gesture, hence press(): the caller feeds it the match's real presses.
// Three failed requests in a row stop the retries for the rest of the activation (an unsupported or
// policy-blocked page must not re-request on every click); a success resets the count. Chrome's short
// refusal window right after an unlock neither counts nor gets asked into (see QUIET_MS).

export type Point = { x: number; y: number };
export type Rect = { left: number; top: number; width: number; height: number };

/** Keeps a point inside the rect; the far edges are exclusive (elementFromPoint at `left + width` is
    already outside the stage). */
export function clampToRect(p: Point, r: Rect): Point {
  return {
    x: Math.min(Math.max(p.x, r.left), r.left + Math.max(0, r.width - 1)),
    y: Math.min(Math.max(p.y, r.top), r.top + Math.max(0, r.height - 1)),
  };
}

/** One locked move: `movementX/Y` times `scale` (movement units → CSS px), clamped to the stage. */
export function integrate(p: Point, dx: number, dy: number, scale: number, r: Rect): Point {
  return clampToRect({ x: p.x + dx * scale, y: p.y + dy * scale }, r);
}

/** Where the virtual cursor starts: the last real cursor position, else the stage's centre. */
export function startPoint(lastReal: Point | null, r: Rect): Point {
  return clampToRect(lastReal ?? { x: r.left + r.width / 2, y: r.top + r.height / 2 }, r);
}

/* The spec puts movementX in CSS px, but browsers have shipped it in device px (DPR-scaled screens) and
   ignoring page zoom; `unadjustedMovement` gives raw counts. Rather than guess per browser, unlocked moves
   — which carry both movementX and clientX — measure the ratio, so a locked move covers the same distance
   the OS cursor would. Until enough travel is seen, 1 (the spec). */
const CALIBRATE_MIN = 50, SCALE_MIN = 0.25, SCALE_MAX = 4, OUTLIER = 2.5, OUTLIER_RUN = 8;
export function createMovementScale() {
  let moved = 0, travelled = 0, rejected = 0;
  const ratio = () => Math.min(SCALE_MAX, Math.max(SCALE_MIN, travelled / moved));
  return {
    /** One unlocked move: its movementX/Y and how far clientX/Y actually went. A sample far off the
        ratio so far (a jump the movement never covered) is dropped once the ratio is established — but
        a run of them means the ratio itself changed (page zoom, a screen with another DPR): start over. */
    sample(mx: number, my: number, cx: number, cy: number) {
      const m = Math.abs(mx) + Math.abs(my), c = Math.abs(cx) + Math.abs(cy);
      if (m === 0 || c === 0) return;
      if (moved >= CALIBRATE_MIN) {
        const k = c / m / ratio();
        if (k > OUTLIER || k < 1 / OUTLIER) {
          if (++rejected < OUTLIER_RUN) return;
          this.reset();
        }
      }
      rejected = 0;
      moved += m; travelled += c;
    },
    /** Forget the ratio (a resize: page zoom may have changed it). */
    reset() { moved = 0; travelled = 0; rejected = 0; },
    get value() { return moved < CALIBRATE_MIN ? 1 : ratio(); },
  };
}

/** The browser's Pointer Lock API, narrowed to what the machine uses. */
export type LockApi = {
  readonly supported: boolean;
  /** The element holding the lock (document.pointerLockElement). */
  locked(): unknown;
  /** Rejects (or throws) when refused; `unadjusted` asks for raw, unaccelerated movement. */
  request(target: unknown, unadjusted: boolean): Promise<void> | void;
  exit(): void;
  /** pointerlockchange / pointerlockerror; returns the unsubscribe. */
  on(type: 'change' | 'error', cb: () => void): () => void;
};

export type LockState = 'off' | 'idle' | 'pending' | 'locked';
const MAX_FAILS = 3;
// Chrome refuses a new lock for about a second after one ends (Esc) — the time a player takes to click
// again. Presses in QUIET_MS after an unlock or a refusal ask nothing, and a refusal within GRACE_MS of an
// unlock is that throttle, not a sign the page can't lock: it isn't counted towards MAX_FAILS.
const QUIET_MS = 1500, REFUSED_QUIET_MS = 1000, GRACE_MS = 3000, LINGER_MS = 3000;

export function createLockMachine(api: LockApi, target: unknown, opts: {
  enabled: boolean;
  /** Ask for raw, unaccelerated movement. Off by default: the virtual cursor should move like the OS one
   *  (the player's pointer speed and acceleration), and the scale is calibrated from accelerated moves. */
  raw?: boolean;
  onLock(): void;
  /** `forced`: the browser dropped the lock (Esc, focus loss), not setActive(false). */
  onUnlock(forced: boolean): void;
  /** Milliseconds, monotonic; performance.now by default (tests pass a fake clock). */
  now?: () => number;
}) {
  const now = opts.now ?? (() => performance.now());
  let state: LockState = 'off', fails = 0, unadjusted = opts.raw ?? false, asked = 0;
  let quietUntil = -Infinity, unlockedAt = -Infinity, destroyed = false, lingering = false;
  const usable = opts.enabled && api.supported;

  function unlocked() { unlockedAt = now(); quietUntil = unlockedAt + QUIET_MS; }
  function failed() {
    if (state !== 'pending') return;  // the promise and pointerlockerror both report one refusal
    state = 'idle';
    const t = now();
    quietUntil = Math.max(quietUntil, t + REFUSED_QUIET_MS);
    if (t - unlockedAt >= GRACE_MS) fails++;
  }
  /** A lock that lands when nobody wants it (match over, torn down) is given straight back. Once torn
      down, only while the request out at teardown can still land: later, a lock on the same stage is the
      next match's. */
  function unwanted() {
    if (destroyed ? !lingering : state !== 'off') return;
    if (api.locked() === target) api.exit();
  }
  function ask() {
    const retryPlain = unadjusted, n = ++asked;
    let p: Promise<void> | void;
    try { p = api.request(target, unadjusted); } catch { failed(); return; }
    p?.then?.(unwanted, (err: unknown) => {
      if (n !== asked) return;  // settles after a newer request went out: that one reports for itself
      // unadjustedMovement unsupported on this OS/browser: ask again without it, still inside the gesture.
      if (retryPlain && (err as { name?: string } | null)?.name === 'NotSupportedError' && state === 'pending') {
        unadjusted = false;
        ask();
        return;
      }
      failed();
    });
  }
  const onChange = () => {
    const ours = api.locked() === target;
    if (ours && state === 'off') { unwanted(); return; }  // granted after the match stopped wanting it
    if (ours && state !== 'locked') { state = 'locked'; fails = 0; opts.onLock(); return; }
    if (!ours && state === 'locked') { state = 'idle'; unlocked(); opts.onUnlock(true); return; }
    if (!ours && state === 'pending') failed();
  };
  const offChange = api.on('change', onChange);
  const offError = api.on('error', failed);

  return {
    get state() { return state; },
    /** In a live match (true) or not (false: ended, spectating, torn down). */
    setActive(active: boolean) {
      if (active) { if (state === 'off' && !destroyed) { state = 'idle'; fails = 0; } return; }
      if (state === 'off') return;
      const was = state;
      state = 'off';
      if (was === 'locked' || api.locked() === target) api.exit();
      if (was === 'locked') { unlocked(); opts.onUnlock(false); }
    },
    /** A real press in the match: the user gesture a lock request needs. */
    press() {
      if (!usable || state !== 'idle' || fails >= MAX_FAILS || now() < quietUntil) return;
      state = 'pending';
      ask();
    },
    destroy() {
      const inFlight = state === 'pending';
      this.setActive(false);
      destroyed = true;
      offError(); offChange();
      if (!inFlight) return;
      // The request still out may yet be granted (Firefox/Safari report that only by pointerlockchange):
      // listen until it settles either way — or LINGER_MS, if it never does — to give that lock back.
      lingering = true;
      const done = () => { lingering = false; offC(); offE(); clearTimeout(timer); };
      const offC = api.on('change', () => { unwanted(); done(); });
      const offE = api.on('error', done);
      const timer = setTimeout(done, LINGER_MS);
    },
  };
}

/** The real API on `doc`. Firefox/Safari return undefined from requestPointerLock and ignore options.
    Unsupported (or a DOM-less test double): never asks, and subscribes to nothing. */
export function browserLockApi(doc: Document): LockApi {
  const supported = typeof doc.exitPointerLock === 'function' && typeof doc.addEventListener === 'function'
    && typeof (doc.documentElement as Element | null)?.requestPointerLock === 'function';
  if (!supported) return { supported, locked: () => null, request() {}, exit() {}, on: () => () => {} };
  return {
    supported,
    locked: () => doc.pointerLockElement,
    request: (target, unadjusted) => (target as Element).requestPointerLock(unadjusted ? { unadjustedMovement: true } : undefined),
    exit: () => doc.exitPointerLock(),
    on(type, cb) {
      const name = type === 'change' ? 'pointerlockchange' : 'pointerlockerror';
      doc.addEventListener(name, cb);
      return () => doc.removeEventListener(name, cb);
    },
  };
}

/** ?vcursor=1: a stand-in that grants every request and drops the lock on Esc, like a browser does —
    for headless browsers without pointer lock. The real cursor keeps moving; virtualPointer.ts then
    reads movement from clientX/Y deltas instead of movementX/Y. */
export function devLockApi(win: Pick<Window, 'addEventListener' | 'removeEventListener'>): LockApi & { readonly dev: true } {
  let holder: unknown = null;
  const subs = { change: new Set<() => void>(), error: new Set<() => void>() };
  const set = (t: unknown) => { holder = t; queueMicrotask(() => subs.change.forEach(cb => cb())); };
  const onKey = (e: Event) => { if ((e as KeyboardEvent).code === 'Escape' && holder) set(null); };
  return {
    dev: true,
    supported: true,
    locked: () => holder,
    request: target => { set(target); },
    exit: () => { if (holder) set(null); },
    on(type, cb) {
      subs[type].add(cb);
      if (type === 'change') win.addEventListener('keydown', onKey, true);
      return () => { subs[type].delete(cb); if (type === 'change') win.removeEventListener('keydown', onKey, true); };
    },
  };
}
